import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Subscription } from 'rxjs';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useService } from '../../../services/services';
import { LadFieldItem } from './interfaces/lad-field-item';
import { FormMode } from '../../../enums/form-mode';
import DashboardLoading from '../../dashboard/components/dashboard-loading';
import StaticTable, {
  StaticTableColumn,
} from '../../../components/common/table/static-table';
import { useErrorModal } from './components/use-error-modal';
import { SchemaContext, StepProps } from './create';
import css from './index.module.css';

export default function StepLadFields({ mode, setIsChildLoading }: StepProps) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.vaad_liz_reports_v2.lad_fields.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, id, year } = useParams<{
    farmId: string;
    id: string;
    year: string;
  }>();
  const { vaadLizReportV2Service: vaadLizReportService } = useService();
  const [tableData, setTableData] = useState<{ rows: LadFieldItem[] } | null>(
    null
  );
  const { errorModal, showError } = useErrorModal();
  const { setSchema } = useContext(SchemaContext);

  const createSchema = useCallback(() => {
    return yup.object().shape({});
  }, []);

  const sortLadFields = useCallback((a: LadFieldItem, b: LadFieldItem) => {
    const fieldNrComparison = Number(a.field_nr) - Number(b.field_nr);
    if (fieldNrComparison !== 0) return fieldNrComparison;

    return a.id.localeCompare(b.id);
  }, []);

  const onDataError = useCallback(
    (error) => {
      console.error('Error fetching data', error);
      showError(error);
      setIsChildLoading(false);
    },
    [setIsChildLoading, showError]
  );

  const onDataLoad = useCallback(
    (data: LadFieldItem[]) => {
      data.sort(sortLadFields);

      const tableData: LadFieldItem[] = data.map((row: LadFieldItem) => {
        const tableRow = { ...row };
        tableRow.crop_group_value = `${tableRow.crop_group_value} (${tableRow.crop_value})`;
        tableRow.EKO5 = tableRow.eko5Activities;
        return tableRow;
      });

      setSchema(createSchema());
      setTableData({ rows: tableData });
      setIsChildLoading(false);
    },
    [createSchema, setIsChildLoading, setSchema, sortLadFields]
  );

  useEffect(() => {
    setIsChildLoading(true);

    const subscription: Subscription = (
      mode === FormMode.edit
        ? vaadLizReportService.editReportsData(farmId, id, year)
        : vaadLizReportService.getLadFields(farmId, id, year)
    ).subscribe(onDataLoad, onDataError);

    return () => subscription.unsubscribe();
  }, [
    vaadLizReportService,
    farmId,
    id,
    year,
    onDataLoad,
    onDataError,
    mode,
    setIsChildLoading,
  ]);

  const columns: StaticTableColumn<LadFieldItem>[] = [
    { accessor: 'field_nr', header: tp('field_nr'), className: css['strong'] },
    { accessor: 'field_block_nr', header: tp('field_block_nr') },
    { accessor: 'field_name', header: tp('field_name') },
    { accessor: 'area', header: tp('area') },
    { accessor: 'crop_group_value', header: tp('crop_group_value') },
    { accessor: 'crop_culture_code', header: tp('crop_culture_code') },
    { accessor: 'support_types', header: tp('support_types') },
    { accessor: 'EKO5', header: tp('EKO5') },
  ];

  return !tableData ? (
    <>
      <DashboardLoading />

      {errorModal}
    </>
  ) : (
    <div className={css['table']}>
      <StaticTable<LadFieldItem>
        data={tableData}
        keyAccessor="id"
        columns={columns}
        fixedHeight
      />
    </div>
  );
}
